import { format, parseISO } from "date-fns";
import { srLatn } from "date-fns/locale";
import {
  BadgeCheck,
  Check,
  Compass,
  Gift,
  HeartHandshake,
  Info,
  Languages,
  MessageSquare,
  Star,
  User,
  Users,
  Video,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { type Person, type WithContext } from "schema-dts";

import JsonLd from "~/components/json-ld/json-ld";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { buttonVariants } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { VideoIntroductionDialog } from "~/components/ui/video-introduction-dialog";
import { FOCUS_AREA_LABELS, MODALITY_LABELS } from "~/lib/matching/labels";

import { TherapistCardAttribution } from "./card-attribution";
import { ServicePrice, ServicePriceFallback } from "./service-price";
import {
  countWithNoun,
  resolveService,
  REVIEW_FORMS,
  sessionsLabel,
  type TherapistCardProps,
} from "./types";
import { TherapistVideoPreview } from "../therapist-video-preview";
import { TherapistAvailabilityPreview } from "../therapist-availability-preview";

import { APPROACH_OPTIONS } from "~/lib/matching/practice-profile";

const AI_THERAPIST_SLUG_MARKER = "ai-terapeut";

const SUBTLE_ACTION_LINK_CLASS =
  "inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const PORTRAIT_FRAME_CLASS =
  "rounded-lg bg-editorial-quiet ring-8 ring-secondary";

function focusAreaLabel(key: string): string {
  return (FOCUS_AREA_LABELS as Record<string, string>)[key] ?? key;
}

function modalityLabelFor(key: string): string | undefined {
  return (
    APPROACH_OPTIONS.find((option) => option.key === key)?.label ??
    (MODALITY_LABELS as Record<string, string>)[key]
  );
}

/**
 * The video/photo/initials branch, shared by the sm+ side portrait and the
 * mobile top photo so the logic exists once instead of duplicated per
 * breakpoint. The caller owns sizing, aspect ratio and the hover-zoom group.
 */
function PortraitMedia({
  therapist,
  sizes,
}: {
  therapist: TherapistCardProps["therapist"];
  sizes: string;
}) {
  if (therapist.videoIntroductionUrl) {
    return (
      <VideoIntroductionDialog
        videoUrl={therapist.videoIntroductionUrl}
        therapistName={therapist.name}
        therapistImage={therapist.image}
      >
        <button
          type="button"
          className="focus-visible:ring-brand relative block h-full w-full focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset"
          aria-label={`Pogledaj video predstavljanje terapeuta ${therapist.name}`}
        >
          {therapist.image ? (
            <Image
              src={therapist.image}
              alt=""
              fill
              sizes={sizes}
              className="therapist-portrait-media rounded-lg object-cover transition-transform duration-300 ease-out group-hover/card:scale-105"
            />
          ) : (
            <div className="text-muted-foreground flex h-full items-center justify-center text-2xl font-medium">
              {therapist.name[0] ?? "T"}
            </div>
          )}
          <TherapistVideoPreview
            src={therapist.videoIntroductionUrl}
            fit="cover"
          />
          <span className="bg-primary absolute right-2 bottom-2 z-10 flex size-7 items-center justify-center rounded-full shadow-lg ring-2 ring-white transition-transform group-hover:scale-110">
            <Video className="text-primary-foreground size-3.5" />
          </span>
        </button>
      </VideoIntroductionDialog>
    );
  }

  if (therapist.image) {
    return (
      <Image
        src={therapist.image}
        alt=""
        fill
        sizes={sizes}
        className="therapist-portrait-media rounded-lg object-cover transition-transform duration-300 ease-out group-hover/card:scale-105"
      />
    );
  }

  return (
    <div className="text-muted-foreground flex h-full items-center justify-center text-2xl font-medium">
      {therapist.name[0] ?? "T"}
    </div>
  );
}

export function TherapistCard({
  therapist,
  isCouplesTherapy,
  availability,
  match,
  missingAttributes,
  discoverySurface,
  specialtySlug,
  preserveCouplesQuery = false,
  resultRank,
  renderJsonLd = true,
  primaryAction,
}: TherapistCardProps) {
  const isAiTherapist = therapist.slug.includes(AI_THERAPIST_SLUG_MARKER);
  const service = resolveService(therapist, isCouplesTherapy);
  const ServiceIcon = service.type === "COUPLES" ? Users : User;
  const profileHref = `/terapeuti/${therapist.slug}${
    preserveCouplesQuery ? "?couples=1" : ""
  }`;
  const bookingHref = `/terapeuti/${therapist.slug}/book?therapyType=${service.type}${
    preserveCouplesQuery ? "&couples=1" : ""
  }`;

  const focusLabels = therapist.focusAreasTop
    .slice(0, 3)
    .map((key) => therapist.focusAreaLabels?.[key] ?? focusAreaLabel(key));
  const modalityLabel = therapist.modalityPrimary
    ? (therapist.modalityLabel ?? modalityLabelFor(therapist.modalityPrimary))
    : undefined;

  /*
   * Three bullets, each answering a different question a visitor actually
   * decides on: what they treat, how they work, what language you'd speak.
   */
  const bullets = [
    focusLabels.length > 0
      ? { icon: HeartHandshake, text: `Radi sa: ${focusLabels.join(", ")}` }
      : null,
    modalityLabel ? { icon: Compass, text: modalityLabel } : null,
    therapist.languages.length > 1
      ? { icon: Languages, text: therapist.languages.join(", ") }
      : null,
  ].filter(
    (bullet): bullet is { icon: typeof HeartHandshake; text: string } =>
      bullet !== null,
  );

  const offerAvailability =
    availability.state === "break"
      ? ("https://schema.org/OutOfStock" as const)
      : availability.state === "ready"
        ? availability.days.some((day) => day.slotCount > 0)
          ? ("https://schema.org/InStock" as const)
          : ("https://schema.org/OutOfStock" as const)
        : undefined;
  const therapistJsonLd: WithContext<Person> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: therapist.name || "Terapeut",
    jobTitle: therapist.tier ?? undefined,
    image: therapist.image ?? undefined,
    worksFor: {
      "@type": "Organization",
      name: "Rešimo Probleme",
      url: "https://www.resimoprobleme.com",
    },
    makesOffer: {
      "@type": "Offer",
      price: service.price,
      priceCurrency: "EUR",
      availability: offerAvailability,
      itemOffered: {
        "@type": "Service",
        name: "Online psihoterapija",
        provider: {
          "@type": "Person",
          name: therapist.name || "Terapeut",
          jobTitle: therapist.tier ?? "",
          image: therapist.image ?? "",
        },
        areaServed: "Serbia",
        serviceType: "Psihoterapija",
      },
    },
  };

  return (
    <TherapistCardAttribution
      therapistId={therapist.id}
      therapistSlug={therapist.slug}
      discoverySurface={discoverySurface}
      specialtySlug={specialtySlug}
      resultRank={resultRank}
    >
      <article
        data-testid="therapist-card"
        className="group/card border-border/70 bg-card overflow-hidden rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
      >
        {renderJsonLd && <JsonLd data={therapistJsonLd} />}

        <div className="grid sm:min-h-72 sm:grid-cols-[11rem_1.15fr_1fr] md:min-h-80 md:grid-cols-[14rem_1.15fr_1fr] lg:grid-cols-[16rem_1.15fr_1fr]">
          <div className="p-4 pb-0 sm:p-5 sm:pr-0">
            <div
              className={`group relative aspect-4/3 w-full overflow-hidden sm:aspect-auto sm:h-full ${PORTRAIT_FRAME_CLASS}`}
            >
              <PortraitMedia
                therapist={therapist}
                sizes="(min-width: 1024px) 16rem, (min-width: 768px) 14rem, (min-width: 640px) 11rem, calc(100vw - 2rem)"
              />
            </div>
          </div>

          <div className="sm:border-border/60 p-4 sm:border-r sm:p-5">
            <div className="min-w-0 flex-1">
              {/* Labels, not actions. "Preporučujemo" is first-person, so
                    it needs no gender agreement. */}
              {match?.isBest && (
                <p className="mb-1 flex flex-wrap items-center gap-1">
                  <span className="bg-brand-action text-brand-foreground inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[0.6875rem] font-semibold">
                    Najbolje podudaranje
                  </span>
                </p>
              )}

              {/* The name is the profile link: /terapeuti feeds the
                    /terapija cluster, so a JS-only card click is invisible
                    to crawlers. */}
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <Link
                  href={profileHref}
                  prefetch={false}
                  className="hover:text-brand text-base leading-tight font-semibold tracking-tight underline-offset-4 transition-colors hover:underline sm:group-hover/card:underline"
                >
                  {therapist.name}
                </Link>
                {therapist.isVerified && (
                  <BadgeCheck
                    className="text-brand size-4 shrink-0 self-center"
                    aria-label="Profil je pregledan"
                  />
                )}
              </p>
              {therapist.title && (
                <p className="text-muted-foreground mt-0.5 text-sm">
                  {therapist.title}
                </p>
              )}
              {therapist.tier && (
                <p className="text-muted-foreground/80 mt-0.5 line-clamp-1 text-xs">
                  {therapist.tier}
                </p>
              )}
              {(therapist.offersIntroCall || therapist.isFeatured) && (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {therapist.offersIntroCall && (
                    <span className="bg-editorial-quiet text-brand inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[0.6875rem] font-semibold">
                      <Gift className="size-2.5" aria-hidden="true" />
                      Besplatan uvodni razgovor
                    </span>
                  )}
                  {therapist.isFeatured && (
                    <span className="bg-secondary text-muted-foreground inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[0.6875rem] font-medium">
                      Preporučujemo
                    </span>
                  )}
                </div>
              )}
              {therapist.reviewCount > 0 && (
                <p className="mt-1 flex items-center gap-1.5">
                  <Star
                    className="size-4 fill-amber-400 text-amber-400"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold tabular-nums">
                    {countWithNoun(therapist.reviewCount, REVIEW_FORMS)}
                  </span>
                  {therapist.sessionsHeld >= 20 && (
                    <span className="text-muted-foreground text-sm">
                      · {sessionsLabel(therapist.sessionsHeld)}
                    </span>
                  )}
                </p>
              )}
            </div>

            {bullets.length > 0 && (
              <ul className="border-border/60 mt-3.5 space-y-1.5 border-t pt-3.5">
                {bullets.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex gap-2 text-sm">
                    <Icon
                      className="text-brand mt-0.5 size-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-foreground min-w-0 flex-1 leading-snug">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Price is information, not an action: the calendar is the only
                way to book. The AI therapist has neither a price nor a
                calendar, only a single conversation action on the right. */}
            {!isAiTherapist && (
              <div className="border-border/60 mt-3.5 border-t pt-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground flex items-center gap-2 text-sm">
                    <ServiceIcon
                      className="text-muted-foreground size-4 shrink-0"
                      aria-hidden="true"
                    />
                    {service.label} · {service.minutes} min
                  </span>
                  <Suspense
                    fallback={<ServicePriceFallback price={service.price} />}
                  >
                    <ServicePrice price={service.price} />
                  </Suspense>
                </div>
              </div>
            )}
          </div>

          {/* The identity column's height varies with how many bullets and
              chips a therapist has, so this column centers its content rather
              than pinning it to the top and leaving a gap below on the
              shorter cards. */}
          <div className="border-border/60 flex flex-col justify-center border-t p-4 sm:border-t-0 sm:p-5">
            {primaryAction ? (
              primaryAction
            ) : isAiTherapist ? (
              <div className="flex items-center justify-center py-4">
                <Link
                  href={profileHref}
                  prefetch={false}
                  className="bg-brand-action text-brand-foreground hover:bg-brand-action-hover inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
                >
                  <MessageSquare className="size-4" aria-hidden="true" />
                  Započni razgovor
                </Link>
              </div>
            ) : availability.state === "break" ? (
              <BreakNotice
                profileHref={profileHref}
                until={availability.until}
              />
            ) : availability.state === "loading" ? (
              <LoadingTiles />
            ) : availability.state === "unavailable" ? (
              <UnavailableNotice
                profileHref={profileHref}
                bookingHref={bookingHref}
              />
            ) : (
              <TherapistAvailabilityPreview
                slug={therapist.slug}
                therapyType={service.type}
                preserveCouplesQuery={preserveCouplesQuery}
                profileHref={profileHref}
                days={availability.days}
              />
            )}
          </div>
        </div>

        {missingAttributes && missingAttributes.length > 0 && (
          <div className="border-border/60 border-t px-4 py-3.5 sm:px-5">
            <Alert variant="warning" className="rounded-xl">
              <AlertTitle className="text-sm font-medium">
                Nekoliko stvari koje treba da znaš
              </AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-2 text-sm">
                  {missingAttributes.map((missing) => (
                    <li key={missing} className="flex gap-2.5">
                      <Info
                        className="mt-0.5 size-4 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="leading-5">{missing}</span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Match reasoning sits under the whole card, full width, so the
            generated sentences get the width they need instead of squeezing
            one column. */}
        {match && match.reasons.length > 0 && (
          <div className="border-border/60 bg-editorial-quiet border-t px-4 py-3.5 sm:px-5">
            {/* "Zašto ti X odgovara" needs no gender agreement, unlike the
                old card's "Zašto je X dobar izbor". */}
            <p className="text-foreground text-sm font-semibold">
              Zašto ovaj terapeut
            </p>
            <ul className="mt-2 space-y-1.5">
              {match.reasons.map((reason) => (
                <li key={reason} className="flex gap-2 text-sm">
                  <Check
                    className="text-brand mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-foreground min-w-0 flex-1 leading-snug">
                    {reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mobile only: sm+ already puts the name and a profile-linked photo
            side by side, so a repeated link would be redundant there. Styled
            directly with buttonVariants: Button asChild(Link) throws
            React.Children.only. */}
        <div className="border-border/60 border-t p-4 sm:hidden">
          <Link
            href={profileHref}
            prefetch={false}
            className={buttonVariants({
              variant: "ghost",
              className: "ring-border w-full ring-1 ring-inset",
            })}
          >
            Pogledaj profil
          </Link>
        </div>
      </article>
    </TherapistCardAttribution>
  );
}

function BreakNotice({
  profileHref,
  until,
}: {
  profileHref: string;
  until: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-6 text-center">
      <p className="text-muted-foreground text-sm">
        Na odmoru do {format(parseISO(until), "d. MMMM", { locale: srLatn })}
      </p>
      <Link
        href={profileHref}
        prefetch={false}
        className="text-brand text-sm font-medium hover:underline"
      >
        Pogledaj profil
      </Link>
    </div>
  );
}

function UnavailableNotice({
  profileHref,
  bookingHref,
}: {
  profileHref: string;
  bookingHref: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-6 text-center">
      <p className="text-muted-foreground text-sm">
        Pregled termina trenutno nije dostupan
      </p>
      <Link
        href={bookingHref}
        prefetch={false}
        className="text-brand text-sm font-medium hover:underline"
      >
        Otvori ceo kalendar
      </Link>
      <Link
        href={profileHref}
        prefetch={false}
        className={SUBTLE_ACTION_LINK_CLASS}
      >
        Pogledaj profil
      </Link>
    </div>
  );
}

function LoadingTiles() {
  return (
    <div>
      <Skeleton className="mx-auto h-4 w-48" />
      <div className="mt-3 grid grid-cols-4 gap-2">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-17 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export type { TherapistCardProps } from "./types";
